import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { cn } from '@workspace/ui/lib/utils';

import { company } from '~/company/Company';
import { HeartIcon, LinkIcon } from '~/components/MarketplaceIcons';
import CheckIcon from '~/components/MarketplaceIcons/CheckIcon';
import { useMounted } from '~/hooks/useMounted';
import { useCopyLink } from '~/pages/DubaiExpress/hooks/useCopyLink';
import type { ProductViewProps } from '~/pages/Product/Product';
import { userStore } from '~/stores';
import { favoritesStore } from '~/stores/FavoritesStore/FavoritesStore';

type Props = Pick<ProductViewProps, 'product' | 'handleFavorite'> & {
  className?: string;
};

const SQUARE_CLASS =
  'box-border flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-[8px] border border-[#E1E1E1] border-solid bg-white p-0 transition-colors duration-[120ms] ease-[cubic-bezier(.22,1,.36,1)] hover:border-[#141415]';

/** Favorites and copy-link squares: beside the CTA on desktop, over the gallery on mobile. */
const ProductDEActionsView = ({
  product,
  handleFavorite,
  className,
}: Props) => {
  const { t } = useTranslation();
  const mounted = useMounted();
  const { copied, copyLink } = useCopyLink();
  const isFavorite = mounted && favoritesStore.isFavorited(product.data?.id);
  const showFavorite =
    !company.hideFavoritesForGuests || (mounted && userStore.isAuthorized);

  return (
    <div className={cn('flex shrink-0 gap-2', className)}>
      {showFavorite && (
        <button
          type="button"
          aria-label={
            isFavorite ? t('favoritesDE:remove') : t('favoritesDE:add')
          }
          onClick={handleFavorite}
          className={cn(
            SQUARE_CLASS,
            isFavorite ? 'text-[#E94E1A]' : 'text-[#141415]',
          )}
        >
          <HeartIcon filled={isFavorite} className="size-5" />
        </button>
      )}
      <button
        type="button"
        aria-label={copied ? t('linkCopied') : t('productDE:copyLink')}
        onClick={copyLink}
        className={cn(
          SQUARE_CLASS,
          copied ? 'text-[#1E9E52]' : 'text-[#141415]',
        )}
      >
        {copied ? (
          <CheckIcon className="size-5" />
        ) : (
          <LinkIcon className="size-5" />
        )}
      </button>
    </div>
  );
};

export const ProductDEActions = observer(ProductDEActionsView);
